import { useState } from 'react';
import type { ReactElement } from 'react';
import { beschreibeAuswahlregel, pruefeAuswahl, wendeAuswahlAn } from '@mcikm-vote/shared';
import type { Abstimmungsoption, TeilnehmerAbstimmung } from '@mcikm-vote/shared';

import { erzeugeKennung } from '../../../browser';
import { Fehlermeldung } from '../../../components/Fehlermeldung';
import { FormatierterText } from '../../../components/FormatierterText';
import { Karte } from '../../../components/Karte';
import { Knopf } from '../../../components/Knopf';
import { useStimmeAbgeben } from '../../../teilnehmer/teilnehmerApi';

/**
 * Aktive Abstimmung und Bestätigung (§13.1, §19.2).
 *
 * Der Ablauf folgt §13.1 Schritt für Schritt: auswählen, prüfen, senden nur bei gültiger Auswahl,
 * Zusammenfassung ansehen, zurückgehen oder endgültig bestätigen.
 *
 * **Die Auswahl lebt ausschließlich im Speicher dieses Tabs** (§40.3, §9.7). Sie liegt nicht in
 * `localStorage`, wird nicht zwischen Tabs abgeglichen und ist nach der Stimmabgabe fort — die
 * Serverantwort enthält sie ohnehin nicht mehr (§13.1 Schritt 9, §13.2).
 *
 * **Die Vorgangskennung entsteht einmal je Absendevorgang** und bleibt bei einem erneuten Versuch
 * dieselbe. Genau das verlangt §13.3: eine Wiederholung nach Verbindungsabbruch darf nicht doppelt
 * zählen.
 *
 * **Ohne bestätigte Serververbindung wird nicht abgestimmt** (§22.2). Das ist Benutzerführung und
 * keine Autorisierung — verbindlich prüft der Server (§25.2). Der Sinn ist ein anderer: Eine
 * Stimme, die ins Leere liefe, hinterließe beim Teilnehmer den Eindruck, er habe abgestimmt.
 */
export function AbstimmungAnsicht({
  abstimmung,
  verbindungBestaetigt,
}: {
  abstimmung: TeilnehmerAbstimmung;
  /** Ob eine Serververbindung besteht — über den Echtzeitkanal oder die letzte Zustandsabfrage. */
  verbindungBestaetigt: boolean;
}): ReactElement {
  const abgeben = useStimmeAbgeben();

  const [gewaehlt, setGewaehlt] = useState<Abstimmungsoption[]>([]);
  const [bestaetigt, setBestaetigt] = useState(false);
  const [vorgangskennung, setVorgangskennung] = useState<string | undefined>(undefined);

  const regeln = {
    auswahlart: abstimmung.auswahlart,
    stimmenanzahl: abstimmung.stimmenanzahl,
    auswahlregel: abstimmung.auswahlregel,
  };

  // §13.1 Schritt 2: dieselbe Prüfung, die auch der Server anwendet — hier nur zur Führung.
  const pruefung = pruefeAuswahl(
    regeln,
    gewaehlt.map((option) => option.art),
  );
  const mehrfach = abstimmung.auswahlart === 'mehrfach';

  function klick(option: Abstimmungsoption): void {
    setGewaehlt((bisher) => wendeAuswahlAn(regeln, bisher, option));
  }

  function zurBestaetigung(): void {
    if (!pruefung.gueltig) {
      return;
    }
    // Die Kennung entsteht hier, nicht erst beim Senden: ein zweiter Anlauf nach einem
    // Verbindungsabbruch verwendet dieselbe (§13.3). Über `erzeugeKennung()` und nicht über
    // `crypto.randomUUID()`, das es ohne sicheren Kontext nicht gibt — siehe `browser.ts`.
    setVorgangskennung(erzeugeKennung());
    setBestaetigt(true);
  }

  function endgueltigSenden(): void {
    if (vorgangskennung === undefined || !pruefung.gueltig || !verbindungBestaetigt) {
      return;
    }
    abgeben.mutate({
      abstimmungId: abstimmung.id,
      optionIds: gewaehlt.map((option) => option.id),
      vorgangskennung,
    });
  }

  const kopf = (
    <Karte>
      <p className="text-text-gedaempft text-sm font-medium">
        Abstimmung {abstimmung.nummer ?? '—'}
      </p>
      <h2 className="mt-1 text-xl font-semibold">{abstimmung.ueberschrift}</h2>
      <div className="mt-3">
        <FormatierterText text={abstimmung.text} />
      </div>
    </Karte>
  );

  if (bestaetigt && pruefung.gueltig) {
    return (
      <div className="flex flex-col gap-4">
        {kopf}

        <Karte betont>
          <h3 className="text-base font-semibold">Bitte bestätigen Sie Ihre Auswahl</h3>

          <ul className="mt-3 flex flex-col gap-2">
            {gewaehlt.map((option) => (
              <li
                key={option.id}
                className={[
                  'border-rand bg-hintergrund rounded-md border p-3 text-lg font-medium',
                  option.art === 'regulaer' ? '' : 'italic',
                ].join(' ')}
              >
                {option.text}
              </li>
            ))}
          </ul>

          <p className="text-warnung mt-3 text-sm font-medium">
            Nach der Bestätigung kann die Stimme weder geändert noch zurückgenommen werden.
          </p>

          {abgeben.isError && <Fehlermeldung>{abgeben.error.message}</Fehlermeldung>}

          {!verbindungBestaetigt && (
            <p className="text-warnung mt-3 text-sm">
              Derzeit besteht keine Verbindung zum Server. Die Stimmabgabe ist erst wieder möglich,
              sobald die Verbindung steht. Ihre Auswahl bleibt so lange erhalten.
            </p>
          )}

          <div className="mt-4 flex flex-col gap-2">
            <Knopf
              breit
              disabled={abgeben.isPending || !verbindungBestaetigt}
              onClick={endgueltigSenden}
            >
              {abgeben.isPending ? 'Stimme wird abgegeben …' : 'Stimme endgültig abgeben'}
            </Knopf>
            <Knopf
              art="sekundaer"
              breit
              disabled={abgeben.isPending}
              onClick={() => {
                setBestaetigt(false);
              }}
            >
              Zurück zur Auswahl
            </Knopf>
          </div>
        </Karte>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {kopf}

      <Karte>
        <fieldset className="flex flex-col gap-2">
          <legend className="mb-2 text-sm font-medium">
            {beschreibeAuswahlregel(
              abstimmung.auswahlart,
              abstimmung.stimmenanzahl,
              abstimmung.auswahlregel,
            )}
          </legend>

          {abstimmung.optionen.map((option) => {
            const ausgewaehlt = gewaehlt.some((eintrag) => eintrag.id === option.id);

            return (
              <label
                key={option.id}
                className={[
                  'min-h-touchflaeche flex cursor-pointer items-center gap-3 rounded-md border p-3',
                  ausgewaehlt
                    ? 'border-akzent bg-akzent/10 font-medium'
                    : 'border-rand hover:bg-flaeche-gedaempft',
                ].join(' ')}
              >
                <input
                  // §11.3 gegen §11.4: eine Auswahl schließt die andere aus, mehrere nicht. Die
                  // Bedienelemente sagen das schon vor dem ersten Klick.
                  type={mehrfach ? 'checkbox' : 'radio'}
                  name="auswahl"
                  value={option.id}
                  checked={ausgewaehlt}
                  onChange={() => {
                    klick(option);
                  }}
                  className="h-5 w-5"
                />
                <span className={option.art === 'regulaer' ? '' : 'italic'}>{option.text}</span>
              </label>
            );
          })}
        </fieldset>

        {/* §13.1 Schritt 3: die Schaltfläche ist nur bei gültiger Auswahl aktiv. */}
        <Knopf breit className="mt-4" disabled={!pruefung.gueltig} onClick={zurBestaetigung}>
          Auswahl senden
        </Knopf>

        {!pruefung.gueltig && (
          <p className="text-text-gedaempft mt-2 text-center text-xs">{pruefung.meldung}</p>
        )}
      </Karte>
    </div>
  );
}
